// src/utils/follow/followApi.js
import axios from "axios";

const apiUrl = process.env.REACT_APP_API_URL || "";

// Create axios instance with default config
const followApiClient = axios.create({
  baseURL: apiUrl,
  timeout: 15000, // 15 seconds timeout
  headers: {
    "Content-Type": "application/json",
  },
});

// Add request interceptor for consistent error handling
followApiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Enhance error with additional context
    if (error.code === "ECONNABORTED") {
      error.message = "Request timeout - please check your connection";
    } else if (!error.response && error.request) {
      error.message = "Network error - please check your internet connection";
    }
    return Promise.reject(error);
  }
);

/**
 * Follow a user
 * @param {string} userId - The ID of the user to follow
 * @param {string} token - Auth0 access token
 * @returns {Promise<Object>} API response data
 */
export const followUser = async (userId, token) => {
  if (!userId || !token) {
    throw new Error("User ID and authentication token are required");
  }

  const { data } = await followApiClient.post(
    `/api/users/${userId}/follow`,
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return data;
};

/**
 * Unfollow a user
 * @param {string} userId - The ID of the user to unfollow
 * @param {string} token - Auth0 access token
 * @returns {Promise<Object>} API response data
 */
export const unfollowUser = async (userId, token) => {
  if (!userId || !token) {
    throw new Error("User ID and authentication token are required");
  }

  const { data } = await followApiClient.delete(`/api/users/${userId}/follow`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return data;
};

/**
 * Get follow status between current user and target user
 * @param {string} userId - The ID of the user to check follow status for
 * @param {string} token - Auth0 access token
 * @returns {Promise<Object>} Follow status data
 */
export const getFollowStatus = async (userId, token) => {
  if (!userId || !token) {
    throw new Error("User ID and authentication token are required");
  }

  const { data } = await followApiClient.get(
    `/api/users/${userId}/follow-status`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return data;
};

/**
 * Get bulk follow status for multiple users
 * @param {string[]} userIds - Array of user IDs to check follow status for
 * @param {string} token - Auth0 access token
 * @returns {Promise<Object>} Bulk follow status data
 */
export const getBulkFollowStatus = async (userIds, token) => {
  if (!userIds || !Array.isArray(userIds) || userIds.length === 0 || !token) {
    throw new Error(
      "Valid user IDs array and authentication token are required"
    );
  }

  const { data } = await followApiClient.post(
    `/api/users/follow-statuses`,
    { userIds },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return data;
};

/**
 * Get paginated list of followers for a user
 * @param {string} userId - The ID of the user to get followers for
 * @param {number} page - Page number (1-based)
 * @param {number} limit - Number of followers per page
 * @param {string} token - Auth0 access token
 * @returns {Promise<Object>} Paginated followers data
 */
export const getFollowers = async (userId, page = 1, limit = 20, token) => {
  if (!userId || !token) {
    throw new Error("User ID and authentication token are required");
  }

  if (page < 1 || limit < 1 || limit > 100) {
    throw new Error("Invalid pagination parameters");
  }

  const { data } = await followApiClient.get(
    `/api/users/${userId}/followers?page=${page}&limit=${limit}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return data;
};

/**
 * Get paginated list of users that a user is following
 * @param {string} userId - The ID of the user to get following list for
 * @param {number} page - Page number (1-based)
 * @param {number} limit - Number of users per page
 * @param {string} token - Auth0 access token
 * @returns {Promise<Object>} Paginated following data
 */
export const getFollowing = async (userId, page = 1, limit = 20, token) => {
  if (!userId || !token) {
    throw new Error("User ID and authentication token are required");
  }

  if (page < 1 || limit < 1 || limit > 100) {
    throw new Error("Invalid pagination parameters");
  }

  const { data } = await followApiClient.get(
    `/api/users/${userId}/following?page=${page}&limit=${limit}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return data;
};

/**
 * Get user profile with follow-related data
 * Uses existing endpoint
 * @param {string} userId - ID of user to get profile for
 * @param {string} token - Auth token
 * @returns {Promise<Object>} User profile data
 */
export const getUserProfile = async (userId, token) => {
  if (!userId || !token) {
    throw new Error("User ID and authentication token are required");
  }

  const { data } = await followApiClient.get(`/api/users/${userId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return data;
};

/**
 * Update user verification status (Admin only)
 * @param {string} userId - ID of user to update
 * @param {boolean} verified - New verification status
 * @param {string} token - Auth token
 * @returns {Promise<Object>} Updated user data
 */
export const updateUserVerification = async (userId, verified, token) => {
  if (!userId || typeof verified !== "boolean" || !token) {
    throw new Error(
      "User ID, verification status, and authentication token are required"
    );
  }

  const { data } = await followApiClient.put(
    `/api/users/${userId}/verify`,
    { verified },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return data;
};

/**
 * Get users list for admin (uses existing endpoint)
 * @param {Object} params - Query parameters
 * @param {string} token - Auth token
 * @returns {Promise<Object>} Users list with pagination
 */
export const getAdminUsersList = async (params, token) => {
  if (!token) {
    throw new Error("Authentication token is required");
  }

  const queryParams = new URLSearchParams(params);
  const { data } = await followApiClient.get(
    `/api/admin/users?${queryParams.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return data;
};
